// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import useResizeObserver from "@react-hook/resize-observer";
import { useEffect, useRef, useState } from "react";

import { mdiDotsVertical, mdiDownload, mdiLoading, mdiPlus } from "@mdi/js";
import { Button, Popover, Typography } from "antd";
import styled from "styled-components";

import { ShapefileAction } from "components/project/layers/components/layer-managers/shared";
import { useSelectedProject } from "components/project/projects/hooks";
import { Toast } from "components/shared/Toast";
import { useFetchScenarioFields } from "components/stick-data-management/hooks";
import { useGetPlaysList } from "components/tools/hooks";

import PadScenarioModal from "./PadScenarioModal";
import { IconButton } from "./ScenarioConfigurationTable";
import { ScenarioHeader } from "./ScenarioHeader";
import ScenarioList from "./ScenarioList";
import { ScenarioTabs } from "./ScenarioTabs";
import { usePadScenario } from "./contexts/usePadScenario";
import { usePadScenarioController } from "./hooks/usePadScenarioController";
import { usePadScenarioPermissions } from "./hooks/usePadScenarioPermissions";

export const ScenarioToastContainerId = "scenario-container";

export default function PadScenario() {
  const { state, dispatch } = usePadScenario();

  const [showNewScenarioModal, setShowNewScenarioModal] = useState(false);
  const [containerHeight, setContainerHeight] = useState(0);
  const containerRef = useRef(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [availablePlays, setAvailablePlays] = useState<string[]>([]);
  const { selectedProject } = useSelectedProject();

  // Fetch plays list once
  const { mutate: fetchPlays } = useGetPlaysList((plays) => {
    setAvailablePlays(plays.filter((play) => play != null));
  });

  useEffect(() => {
    fetchPlays();
  }, []);

  // Fetch scenario fields when scenario changes
  const scenarioId = state.scenario?._id?.$oid;
  const { data: scenarioFieldsData } = useFetchScenarioFields(scenarioId);

  // Update context with fetched fields
  useEffect(() => {
    if (scenarioFieldsData?.fields) {
      dispatch({
        type: "SET_SCENARIO_FIELDS",
        payload: scenarioFieldsData.fields
      });
    } else if (scenarioId && !scenarioFieldsData) {
      // Reset fields when switching to a scenario that hasn't loaded yet
      dispatch({
        type: "SET_SCENARIO_FIELDS",
        payload: []
      });
    }
  }, [scenarioFieldsData, scenarioId, dispatch]);

  useResizeObserver(containerRef, (entry) => {
    setContainerHeight(entry.contentRect.height);
  });

  const {
    handleExportScenario,
    isExporting,
    isLoading,
    editId,
    handleNewRow,
    handleRunScenario
  } = usePadScenarioController(state, dispatch);

  const { showFullPadScenario, isStickManagementMode } = usePadScenarioPermissions();

  return (
    <RootContainer>
      <ScenarioWrapper>
        <ScenarioHeader projectName={selectedProject?.projectName ?? ""} />
        <ScenarioList
          availablePlays={availablePlays}
          title={
            <>
              <Typography.Title level={5} style={{ margin: 0 }}>
                Scenarios
              </Typography.Title>
              {(showFullPadScenario || isStickManagementMode) && (
                <ButtonsWrapper>
                  <Button
                    size="small"
                    type="primary"
                    icon={<Icon path={mdiPlus} size={1.2} />}
                    onClick={() => setShowNewScenarioModal(true)}
                    loading={isLoading}>
                    New
                  </Button>
                  <Popover
                    overlayClassName={"popover-no-padding"}
                    placement={"bottom"}
                    open={popoverOpen}
                    onOpenChange={setPopoverOpen}
                    content={
                      <ActionListWrapper style={{ minWidth: "130px" }}>
                        <ShapefileAction
                          onClick={handleExportScenario}
                          disabled={isExporting}>
                          {isExporting ? (
                            <Icon path={mdiLoading} size={1} spin />
                          ) : (
                            <Icon path={mdiDownload} size={1} />
                          )}
                          Download
                        </ShapefileAction>
                      </ActionListWrapper>
                    }
                    trigger={"click"}>
                    <IconButton
                      type="text"
                      icon={<Icon path={mdiDotsVertical} size={1} />}
                      title="Edit"
                    />
                  </Popover>
                </ButtonsWrapper>
              )}
            </>
          }
        />
        <TabsWrapper ref={containerRef}>
          <ScenarioTabs
            showFullPadScenario={showFullPadScenario}
            isStickManagementMode={isStickManagementMode}
            containerHeight={containerHeight}
            editId={editId}
            onNewRow={handleNewRow}
            onRunScenario={handleRunScenario}
            isLoading={isLoading}
          />
        </TabsWrapper>
      </ScenarioWrapper>
      <PadScenarioModal
        open={showNewScenarioModal}
        mode={"add"}
        onClose={() => setShowNewScenarioModal(false)}
        availablePlays={availablePlays}
      />
      <div id="scenario-modal-container" />
      <Toast toastId="scenario-container" />
    </RootContainer>
  );
}

const RootContainer = styled.div`
  height: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  -ms-flex-direction: column;
  grid-template-rows: minmax(0, 1fr);

  .ant-btn {
    display: flex;
    align-items: center;
  }

  .ant-form-item-label {
    min-width: 150px;
  }
`;

const ScenarioWrapper = styled.div`
  display: grid;
  width: 100%;
  height: 100%;
  grid-template-columns: 300px minmax(0, 1fr);
  grid-template-rows: 40px minmax(0, 1fr);
`;

const TabsWrapper = styled.div`
  padding: 5px;
  height: 100%;
  min-height: 0;

  .ant-tabs,
  .ant-tabs-content,
  .ant-tabs-tabpane {
    height: 100%;
  }
`;

const ActionListWrapper = styled.div`
  padding: 6px;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;
